import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './AuthPages.module.css';

// Mirrors inviteService.MIN_PASSWORD_LENGTH — the server re-validates on accept.
const MIN_PASSWORD_LENGTH = 8;

// Seconds the success panel shows before it sends the user to /login.
const REDIRECT_DELAY_MS = 2500;

// Terminal invite-token verdicts from inviteService.classifyInviteAcceptance.
// These can never succeed on retry, so they end the flow on a panel that tells
// the user how to get a new link — resubmitting the form would be a dead end.
const TERMINAL_CODES = ['INVALID', 'ALREADY_USED', 'REVOKED', 'EXPIRED'];

const FALLBACK_INVALID =
  'This invitation link is invalid or has expired. Please ask your administrator to send you a new invitation.';

// The canonical server policy (api/utils/validation.js validatePassword, reached
// via inviteService.validateSetupPassword). Kept in step deliberately: before
// card 28253 this checklist went all-green on `aaaaaaaa` and the server then
// refused it, so the UI told the user something untrue.
const PASSWORD_RULES = [
  {
    key: 'length',
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    isMet: (password) => password.length >= MIN_PASSWORD_LENGTH
  },
  { key: 'uppercase', label: 'One uppercase letter', isMet: (password) => /[A-Z]/.test(password) },
  { key: 'lowercase', label: 'One lowercase letter', isMet: (password) => /[a-z]/.test(password) },
  { key: 'number', label: 'One number', isMet: (password) => /\d/.test(password) }
];

// Live checklist so the strength rules are visible BEFORE submit, not only as
// an error after it.
const buildRequirements = (password, confirmPassword) => [
  ...PASSWORD_RULES.map(({ key, label, isMet }) => ({ key, label, met: isMet(password) })),
  {
    key: 'match',
    label: 'Both passwords match',
    met: password.length > 0 && password === confirmPassword
  }
];

const SetupAccountPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState('loading'); // loading, valid, invalid, success
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [invalidMessage, setInvalidMessage] = useState(FALLBACK_INVALID);
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    // Token is validated on submit - just mark as ready
    setStatus('valid');
  }, [token]);

  // Auto-continue to login once the account is live. Cleared on unmount so a
  // user who clicks the button first is not navigated twice.
  useEffect(() => {
    if (status !== 'success') return undefined;
    const timer = setTimeout(() => navigate('/login'), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status, navigate]);

  const requirements = buildRequirements(password, confirmPassword);

  const validate = () => {
    const errs = {};
    if (!password) errs.password = 'Password is required';
    else {
      const unmet = PASSWORD_RULES.filter(({ isMet }) => !isMet(password));
      if (unmet.length > 0) errs.password = `Password needs: ${unmet.map(({ label }) => label.toLowerCase()).join(', ')}`;
    }
    if (!confirmPassword) errs.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) errs.confirmPassword = 'Passwords do not match';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await api.post('/auth/accept-invite', { token, password });
      setStatus('success');
      toast.success('Account activated successfully!');
    } catch (err) {
      // api.js (fetch-based) throws an Error carrying the server's message plus
      // .status/.code — there is no axios-style err.response here.
      const msg = err.message || 'Failed to activate account';
      toast.error(msg);
      // A dead token can never be retried: show the terminal panel with the
      // server's own explanation instead of leaving the user on the form.
      if (TERMINAL_CODES.includes(err.code)) {
        setInvalidMessage(msg);
        setStatus('invalid');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className={styles.loginPage}>
        <div className={styles.formSide}>
          <div className={styles.formContainer}>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className={styles.loginPage}>
        <div className={styles.formSide}>
          <div className={styles.formContainer}>
            <h2 className={styles.formTitle}>Invalid Invitation</h2>
            <p className={styles.setupNotice}>{invalidMessage}</p>
            <p className={styles.setupHint}>
              Need a new link? Ask your administrator to resend the invitation from
              Roles &amp; Permissions. If you have already set your password, log in below.
            </p>
            <button
              className={styles.loginButton}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className={styles.loginPage}>
        <div className={styles.formSide}>
          <div className={styles.formContainer}>
            <div style={{ textAlign: 'center' }}>
              <div className={styles.setupSuccessMark}>&#10003;</div>
              <h2 className={styles.formTitle}>Account Activated</h2>
              <p className={styles.setupNotice}>
                Your account has been set up successfully. Taking you to the login page&hellip;
              </p>
              <button
                className={styles.loginButton}
                onClick={() => navigate('/login')}
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginPage}>
      <div className={styles.heroSide}>
        <img
          src="/images/log-in-page/login-hero.webp"
          alt="Oval Care"
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Set Up Your Account</h1>
            <p className={styles.heroSubtitle}>
              Create your password to activate your admin access.
            </p>
          </div>
        </div>
      </div>
      <div className={styles.formSide}>
        <div className={styles.formContainer}>
          <div className={styles.formHeader}>
            <img src="/oval-logo.webp" alt="Oval" className={styles.setupLogo} />
            <h2 className={styles.formTitle}>Create Your Password</h2>
            <p className={styles.formSubtitle}>
              Set a secure password to activate your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <label className={styles.setupLabel} htmlFor="setup-password">Password</label>
              <input
                id="setup-password"
                type="password"
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`Minimum ${MIN_PASSWORD_LENGTH} characters`}
                autoFocus
              />
              {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.setupLabel} htmlFor="setup-confirm">Confirm Password</label>
              <input
                id="setup-confirm"
                type="password"
                className={styles.input}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
              />
              {errors.confirmPassword && <span className={styles.fieldError}>{errors.confirmPassword}</span>}
            </div>

            <ul className={styles.setupRequirements}>
              {requirements.map((req) => (
                <li
                  key={req.key}
                  className={req.met ? styles.setupReqMet : styles.setupReq}
                  data-met={req.met ? 'yes' : 'no'}
                >
                  <span className={styles.setupReqMark} aria-hidden="true">
                    {req.met ? '✓' : '○'}
                  </span>
                  {req.label}
                </li>
              ))}
            </ul>

            <button
              type="submit"
              className={styles.loginButton}
              disabled={submitting}
            >
              {submitting ? 'Activating...' : 'Activate Account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetupAccountPage;
